import { http } from '@/core/api'
import { i18n } from '@/i18n'
import { formatNumber } from '@/shared/composables/use-format'
import type { JobOrder, JobOrderAudience, JobOrderStatus, JobPublishPlatform } from '@/shared/types'

const JOB_ORDERS = '/enterprise/job-orders'

export const JOB_PUBLISH_PLATFORM_OPTIONS: JobPublishPlatform[] = [
  'recruiter_internal',
  'supplier_portal',
]

export function isJobPublishedOn(
  job: Pick<JobOrder, 'status' | 'publish_platforms'>,
  platform: JobPublishPlatform,
): boolean {
  return job.status === 'open' && (job.publish_platforms ?? []).includes(platform)
}

export interface JobOrderFilters {
  client_id?: number
  status?: JobOrderStatus
  order_audience?: JobOrderAudience
  q?: string
  open_only?: boolean
  assigned_recruiter_id?: number
  bound_onsite_employee_id?: number
  /** `linked` | `missing` — matches list policy-link filter. */
  policy_link?: 'linked' | 'missing'
  /** Validity window overlap (`starts_at` / `ends_at`). */
  valid_from?: string
  valid_to?: string
  page?: number
  per_page?: number
  known_total?: number
  refresh_meta?: boolean
}

export type JobOrderSummary = {
  total: number
  open: number
  target: number
  onboarded: number
  remaining: number
}

export interface JobOrderPayload {
  client_id: number
  client_site_id?: number | null
  client_recruitment_policy_id?: number | null
  enterprise_settlement_rule_id?: number | null
  code?: string
  title: string
  position_title?: string | null
  job_description?: string | null
  description?: string | null
  location?: string | null
  working_hours?: string | null
  work_shift?: string | null
  education_required?: string | null
  experience_required?: string | null
  benefits?: string | null
  quantity_target?: number
  required_quantity?: number | null
  demand_type?: 'monthly' | 'weekly' | 'daily' | 'yearly' | 'by_date' | null
  period_quantity?: number | null
  onboard_date?: string | null
  demand_by_date?: string | null
  allow_over_quota?: boolean
  over_quota_limit?: number | null
  receive_cap?: number | null
  receive_remaining?: number | null
  internal_quota?: number
  supplier_quota?: number
  quantity_working?: number
  quantity_resigned?: number
  salary_min?: number | null
  salary_max?: number | null
  status?: JobOrderStatus
  /** Required by API when pausing or reopening a paused job. */
  status_change_reason?: string | null
  order_audience?: JobOrderAudience
  publish_platforms?: JobPublishPlatform[]
  starts_at?: string | null
  ends_at?: string | null
  bound_onsite_employee_id?: number | null
  owner_employee_id?: number | null
  assigned_recruiter_id?: number | null
  age_rule?: Record<string, unknown>
  gender_rule?: { allowed?: string | null; notes?: string | null }
  salary_policy?: Record<string, unknown>
  reward_policy?: Record<string, unknown>
  settlement_policy?: Record<string, unknown>
  worker_rewards?: import('@/features/jobs/types/job-order-flexible-details').JobOrderWorkerRewardRow[]
  commissions?: import('@/features/jobs/types/job-order-flexible-details').JobOrderCommissionRow[]
  terms?: import('@/features/jobs/types/job-order-flexible-details').JobOrderTermRow[]
  section_contents?: import('@/features/jobs/types/job-order-flexible-details').JobOrderSectionContentRow[]
  compensations?: import('@/features/jobs/types/job-order-compensation').JobOrderCompensationRow[] | unknown[]
  note?: string | null
  priority_level?: 'high' | 'medium' | 'low' | string | null
}

export type JobOrderUpdatePayload = Partial<JobOrderPayload>

function jobOrderListParams(filters: JobOrderFilters = {}) {
  return {
    ...(filters.client_id ? { client_id: filters.client_id } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.order_audience ? { order_audience: filters.order_audience } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.open_only ? { open_only: 1 } : {}),
    ...(filters.assigned_recruiter_id
      ? { assigned_recruiter_id: filters.assigned_recruiter_id }
      : {}),
    ...(filters.bound_onsite_employee_id
      ? { bound_onsite_employee_id: filters.bound_onsite_employee_id }
      : {}),
    ...(filters.policy_link ? { policy_link: filters.policy_link } : {}),
    ...(filters.valid_from ? { valid_from: filters.valid_from } : {}),
    ...(filters.valid_to ? { valid_to: filters.valid_to } : {}),
    ...(filters.page ? { page: filters.page } : {}),
    ...(filters.per_page ? { per_page: filters.per_page } : {}),
    ...(filters.known_total != null && filters.known_total >= 0
      ? { known_total: filters.known_total }
      : {}),
    ...(filters.refresh_meta ? { refresh_meta: 1 } : {}),
  }
}

export const jobsApi = {
  async list(filters: JobOrderFilters = {}): Promise<{
    data: JobOrder[]
    meta?: { total?: number; last_page?: number; current_page?: number; per_page?: number }
  }> {
    const { data } = await http.get(JOB_ORDERS, {
      params: jobOrderListParams(filters),
    })
    return data
  },

  async summary(filters: Omit<JobOrderFilters, 'page' | 'per_page'> = {}): Promise<{ data: JobOrderSummary }> {
    const { data } = await http.get(`${JOB_ORDERS}/summary`, {
      params: jobOrderListParams(filters),
    })
    return data
  },

  async get(id: number): Promise<{ data: JobOrder }> {
    const { data } = await http.get(`${JOB_ORDERS}/${id}`)
    return data
  },

  async nextCode(clientId?: number): Promise<{ data: { code: string } }> {
    const { data } = await http.get<{ data: { code: string } }>(`${JOB_ORDERS}/next-code`, {
      params: clientId != null && clientId > 0 ? { client_id: clientId } : undefined,
    })
    return data
  },

  async create(body: JobOrderPayload) {
    const { data } = await http.post(JOB_ORDERS, body)
    return data
  },

  async update(id: number, body: JobOrderUpdatePayload) {
    const { data } = await http.patch(`${JOB_ORDERS}/${id}`, body)
    return data
  },

  async submitApproval(id: number) {
    const { data } = await http.post(`${JOB_ORDERS}/${id}/submit-approval`)
    return data
  },

  async rejectApproval(id: number, note?: string) {
    const { data } = await http.post(`${JOB_ORDERS}/${id}/reject-approval`, { note: note ?? null })
    return data
  },

  async delete(id: number) {
    const { data } = await http.delete(`${JOB_ORDERS}/${id}`)
    return data
  },

  async listRewardPrograms(jobOrderId: number) {
    const { data } = await http.get<{ data: import('@/features/enterprise/types/reward-program').RewardProgram[] }>(
      `${JOB_ORDERS}/${jobOrderId}/reward-programs`,
    )
    return data.data ?? []
  },

  async syncRewardPrograms(jobOrderId: number, programs: unknown[]) {
    const { data } = await http.put<{ data: import('@/features/enterprise/types/reward-program').RewardProgram[] }>(
      `${JOB_ORDERS}/${jobOrderId}/reward-programs`,
      { programs },
    )
    return data.data ?? []
  },
}

export function formatJobSalary(min?: number | null, max?: number | null): string {
  const t = i18n.global.t as (key: string, params?: Record<string, unknown>) => string
  if (min != null && max != null) return `${formatNumber(min)} – ${formatNumber(max)} VND`
  if (min != null) return t('jobs.salaryFrom', { amount: formatNumber(min) })
  if (max != null) return t('jobs.salaryTo', { amount: formatNumber(max) })
  return '—'
}
